import { Card, Colors, Divider, Icon } from "@blueprintjs/core";
import styled from "@emotion/styled";
import { Link, useSearchParams } from "react-router";

import { buildUrl } from "@/components/helpers/url-utils";
import { Box, Flex } from "@/components/layout/flexbox";
import { Media } from "@/components/layout/Media";
import ContentContainer from "@/components/pages_flexible/article_series/ContentContainer";
import { ArticleFilledSvg } from "@/components/pieces/icons/svg/article";
import { getRandomDefaultImg } from "@/components/pieces/learn/image-util";
import {
  INNER_BORDER_COLOR,
  OFFER_COLOR,
  SUBTEXT_COLOR,
} from "@/css/constants";

const IMG_HEIGHT = 180;

function ArticleListItem({ article }) {
  const [searchParams] = useSearchParams();

  const {
    urlSlug,
    title,
    heroImage,
    timeToRead,
    summary,
    currentUserReadStatus,
  } = article;

  const imgUrl = heroImage
    ? `${heroImage}?h=${IMG_HEIGHT * 2}&fm=webp`
    : getRandomDefaultImg();

  const url = buildUrl(`/learn/${urlSlug}`, {
    data: {
      layout: searchParams.get("layout"),
    },
  });

  return (
    <Link to={url}>
      <Flex
        p={[3, 3, 24]}
        style={{
          display: "inline-flex",
          width: "100%",
          gap: 24,
        }}
        alignItems="center"
        flexDirection={["column", "row"]}
      >
        <Box
          width={[1, 200, 200, 348]}
          className="round-corners"
          minHeight={IMG_HEIGHT}
          flexShrink={0}
          style={{
            overflow: "hidden",
            backgroundImage: `url(${imgUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <Box flexGrow={1}>
          <StyledArticleTitle>{title}</StyledArticleTitle>
          <Flex mt={2} alignItems="center" fontSize="16">
            <Box color={SUBTEXT_COLOR}>{timeToRead} min read</Box>
            {currentUserReadStatus &&
            currentUserReadStatus === "READING_COMPLETED" ? (
              <Flex
                ml={2}
                alignItems="center"
                color={OFFER_COLOR}
                fontWeight={600}
              >
                <Icon icon="small-tick" />
                <Box ml={1}>Read</Box>
              </Flex>
            ) : null}
          </Flex>
          <Box mt={3} color={SUBTEXT_COLOR}>
            {summary}
          </Box>
        </Box>
      </Flex>
    </Link>
  );
}

interface ArticleSeriesData {
  totalItemCount?: number;
  completedItemCount?: number;
  inprogressItemCount?: number;
  articles?: Array<{
    urlSlug: string;
    title: string;
    heroImage?: string;
    timeToRead: number;
    summary: string;
    currentUserReadStatus: string;
  }>;
  description?: string;
  title?: string;
  urlSlug?: string;
}

function ArticleSeriesHeader({ data }) {
  const { totalItemCount = 0 } = data;

  return (
    <Box className="bbs" bg={Colors.WHITE} width={1}>
      <ContentContainer background="transparent">
        <h1 style={{ fontWeight: "bold" }} className="title">
          {data.title}
        </h1>
        {data.description && (
          <Box mt={3} maxWidth={800} lineHeight={1.5} color={SUBTEXT_COLOR}>
            {data.description}
          </Box>
        )}
        <Box mt={24} pt={3} className="bts">
          <Flex alignItems="center">
            <ArticleFilledSvg size={16} />
            <Box ml={2} color={SUBTEXT_COLOR}>
              {" "}
              {totalItemCount} Articles
            </Box>
          </Flex>
        </Box>
      </ContentContainer>
    </Box>
  );
}

function ArticleSeriesDetailView({
  data = {} as ArticleSeriesData,
}: {
  data?: ArticleSeriesData;
}) {
  const { articles = [] } = data;

  const cardStyles = {
    background: Colors.WHITE,
    border: `1px solid ${INNER_BORDER_COLOR}`,
    boxShadow: "0px 0px 5px rgba(0, 0, 0, 0.05)",
    borderRadius: "10px",
  };

  return (
    <>
      <ArticleSeriesHeader data={data} />
      <Box width={1}>
        <ContentContainer>
          <Card
            elevation={0}
            style={{
              ...cardStyles,
              padding: 0,
            }}
          >
            <ul style={{ listStyle: "none", padding: 0 }}>
              {articles.slice(0, -1).map((article) => (
                <li key={article.urlSlug}>
                  <ArticleListItem key={article.urlSlug} article={article} />
                  <Box px={[0, 24]}>
                    <Divider style={{ margin: 0 }} />
                  </Box>
                </li>
              ))}
              {articles.length ? (
                <li>
                  <ArticleListItem article={articles[articles.length - 1]} />
                </li>
              ) : null}
            </ul>
          </Card>
        </ContentContainer>
      </Box>
    </>
  );
}

export default function ({ data = {} }) {
  return (
    <>
      <Media greaterThan="xs">
        {(mediaClassNames, renderChildren) => {
          return (
            <Box width={1} className={mediaClassNames}>
              {renderChildren ? <ArticleSeriesDetailView data={data} /> : null}
            </Box>
          );
        }}
      </Media>
      <Media at="xs">
        {(mediaClassNames, renderChildren) => {
          return (
            <Box width={1} className={mediaClassNames}>
              {renderChildren ? <ArticleSeriesDetailView data={data} /> : null}
            </Box>
          );
        }}
      </Media>
    </>
  );
}

const StyledArticleTitle = styled.div`
  position: relative;
  margin-top: 0;
  font-size: 24px;
  font-weight: 600;
  line-height: 36px;
  color: ${Colors.BLACK};
`;
